// src/features/leases/components/CurrencyToggle.jsx
//
// LSE-TAB-REDESIGN Part 2 — two-state pill toggle for USD ↔ native
// currency (D2 resolution).
//
// Controlled component:
//   - `nativeCurrency` is the lease's stored currency (lease.currency)
//   - `value` is the currently-selected currency (always either "USD"
//     or nativeCurrency)
//   - `onChange(newCurrency)` fires when the user picks the other side
//
// localStorage persistence is handled by the parent orchestrator
// (LeaseTabTopSection) using leaseTabConfig.currencyTogglePrefKey().
// Keeping the toggle controlled (rather than self-managing storage)
// lets multiple toggles share state if the design ever calls for it,
// and keeps the test surface small.
//
// USD-native leases: the toggle has no meaningful second state, so
// we render a static "USD" pill (non-interactive) — the user sees
// where the toggle WOULD be without a phantom second option. This
// matches the mockup's intent: a UI affordance reserved for FX-aware
// leases.

// INC-02 (LSE-STYLE-NORMALIZE-1): replaced CSS custom property declarations
// with T token references. The CSS vars had fallback hex that didn't exactly
// match T tokens (different shades of dark/mid/light) — now T is the SSOT.
import React from "react";
import { T } from "../../../app/tokens.js";

const LABEL_STYLE = {
  fontSize: T.fsXxs,
  color: T.light,
};

const TOGGLE_STYLE = {
  display: "inline-flex",
  border: `0.5px solid ${T.border}`,
  borderRadius: 4,
  overflow: "hidden",
};

const OPT_BASE = {
  padding: "3px 10px",
  fontSize: T.fsXs,
  cursor: "pointer",
  background: "transparent",
  border: "none",
  fontFamily: "inherit",
};

const OPT_ACTIVE = {
  ...OPT_BASE,
  background: T.dark,
  color: T.card,
  fontWeight: T.fwSemibold,
  cursor: "default",
};

const OPT_INACTIVE = {
  ...OPT_BASE,
  color: T.mid,
};

export function CurrencyToggle({ nativeCurrency, value, onChange }) {
  // Single-state render for USD-native leases — no toggle to offer.
  if (!nativeCurrency || nativeCurrency === "USD") {
    return (
      <div
        data-testid="currency-toggle"
        data-currency-mode="single"
        style={{ display: "flex", alignItems: "center", gap: 8 }}
      >
        <span style={LABEL_STYLE}>Display currency</span>
        <span
          data-testid="currency-toggle-static"
          style={{
            ...OPT_ACTIVE,
            borderRadius: 4,
            border: `0.5px solid ${T.border}`,
          }}
        >
          USD
        </span>
      </div>
    );
  }

  const isUsd = value === "USD";

  function pick(ccy) {
    if (ccy === value) return;
    onChange?.(ccy);
  }

  return (
    <div
      data-testid="currency-toggle"
      data-currency-mode="dual"
      style={{ display: "flex", alignItems: "center", gap: 8 }}
    >
      <span style={LABEL_STYLE}>Display currency</span>
      <div style={TOGGLE_STYLE}>
        <button
          type="button"
          data-testid="currency-toggle-usd"
          aria-pressed={isUsd}
          onClick={() => pick("USD")}
          style={isUsd ? OPT_ACTIVE : OPT_INACTIVE}
        >
          USD
        </button>
        <button
          type="button"
          data-testid="currency-toggle-native"
          aria-pressed={!isUsd}
          onClick={() => pick(nativeCurrency)}
          style={!isUsd ? OPT_ACTIVE : OPT_INACTIVE}
        >
          {nativeCurrency}
        </button>
      </div>
    </div>
  );
}
